import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text } from '../ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import type { SupportCenterAgendaEvent } from '../../types/supportCenterAppointments'
import {
  EMPTY_PSYCH_ATTENDANCE_FORM,
  SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES,
  SUPPORT_CENTER_PSYCH_REFERRALS,
  SUPPORT_CENTER_PSYCH_RISK_LEVELS,
  type SupportCenterPsychAttendanceFormState,
  type SupportCenterPsychAttendancePayload,
  type SupportCenterPsychEmotionalState,
  type SupportCenterPsychReferral,
  type SupportCenterPsychRiskLevel,
} from '../../types/supportCenterPsychAttendance'

const inputClass =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60'

const sectionClass =
  'rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 p-4 space-y-3'

type PsychologicalAttendFormProps = {
  appointment: SupportCenterAgendaEvent
  canSubmit: boolean
  onClose: () => void
  onSubmitted: () => void
  /** Rodapé fixo do modal. Os botões de ação são renderizados nele. */
  actionsHost: HTMLElement | null
}

function todayIsoDate(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function scheduledDateIso(value: string): string {
  if (!value) return todayIsoDate()
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return value.slice(0, 10) || todayIsoDate()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function resolveAttendantName(
  appointment: SupportCenterAgendaEvent,
  profileName: string | null | undefined
): string {
  return (
    appointment.professional?.name?.trim() ||
    (typeof profileName === 'string' ? profileName.trim() : '') ||
    ''
  )
}

function buildInitialForm(
  appointment: SupportCenterAgendaEvent,
  attendantName: string,
  attendanceNumber: string
): SupportCenterPsychAttendanceFormState {
  return {
    ...EMPTY_PSYCH_ATTENDANCE_FORM,
    patient_name: appointment.patient_people?.name?.trim() || '',
    insured_number: appointment.holder_cpf || appointment.patient_cpf || '',
    attendance_date: scheduledDateIso(appointment.scheduled_at),
    attendance_number: attendanceNumber,
    psychologist_name: attendantName,
  }
}

function toPayload(
  appointment: SupportCenterAgendaEvent,
  form: SupportCenterPsychAttendanceFormState
): SupportCenterPsychAttendancePayload {
  const attendanceNumber = Number(form.attendance_number.trim())
  if (!Number.isFinite(attendanceNumber) || attendanceNumber < 1) {
    throw new Error('Número do atendimento inválido (histórico do paciente).')
  }
  if (!form.psychologist_name.trim()) throw new Error('Nome do psicólogo(a) não encontrado.')
  if (!form.demand_notes.trim()) throw new Error('Informe a demanda trazida.')
  if (!form.intervention_notes.trim()) throw new Error('Informe a intervenção realizada.')
  if (!form.emotional_state) throw new Error('Selecione o estado emocional ao final.')
  if (!form.risk_indicator) throw new Error('Selecione o indicador de risco.')
  if (!form.attendance_date) throw new Error('Data do atendimento ausente.')

  const referral =
    form.referral && form.referral !== 'Nenhum' ? form.referral : null

  return {
    appointment_id: appointment.id,
    patient_cpf: appointment.patient_cpf,
    holder_cpf: appointment.holder_cpf,
    specialization_id: appointment.specialization_id,
    professional_user_id: appointment.professional_user_id ?? null,
    patient_name: form.patient_name.trim() || appointment.patient_people?.name || '',
    insured_number: form.insured_number.trim() || null,
    attendance_date: form.attendance_date,
    attendance_number: attendanceNumber,
    psychologist_name: form.psychologist_name.trim(),
    demand_notes: form.demand_notes.trim(),
    intervention_notes: form.intervention_notes.trim(),
    patient_response: form.patient_response.trim() || null,
    emotional_state: form.emotional_state,
    risk_indicator: form.risk_indicator,
    referral,
    next_contact_date: form.next_contact_date.trim() || null,
    additional_notes: form.additional_notes.trim() || null,
  }
}

export const PsychologicalAttendForm: React.FC<PsychologicalAttendFormProps> = ({
  appointment,
  canSubmit,
  onClose,
  onSubmitted,
  actionsHost,
}) => {
  const { profile } = useAuthContext()
  const attendantName = resolveAttendantName(appointment, profile?.name)

  const [form, setForm] = React.useState<SupportCenterPsychAttendanceFormState>(() =>
    buildInitialForm(appointment, attendantName, '')
  )
  const [loadingMeta, setLoadingMeta] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [confirmCompleteOpen, setConfirmCompleteOpen] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoadingMeta(true)
      setError(null)
      setSaving(false)
      setConfirmCompleteOpen(false)
      try {
        const prior = await supportCenterAppointmentsService.countPriorRealizados(
          appointment.patient_cpf,
          appointment.id
        )
        if (cancelled) return
        const nextNumber = String(prior + 1)
        setForm(buildInitialForm(appointment, attendantName, nextNumber))
      } catch (e) {
        if (cancelled) return
        setForm(buildInitialForm(appointment, attendantName, '1'))
        setError(
          e instanceof Error
            ? `Não foi possível carregar o histórico de atendimentos: ${e.message}`
            : 'Não foi possível carregar o histórico de atendimentos.'
        )
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [appointment.id, appointment.patient_cpf, attendantName])

  const requestCompleteConfirm = React.useCallback(() => {
    if (!canSubmit || saving || loadingMeta) return
    try {
      toPayload(appointment, form)
      setError(null)
      setConfirmCompleteOpen(true)
    } catch (e) {
      setConfirmCompleteOpen(false)
      setError(e instanceof Error ? e.message : 'Revise o formulário antes de concluir.')
    }
  }, [appointment, canSubmit, form, loadingMeta, saving])

  const handleSubmit = async () => {
    if (!canSubmit || saving || loadingMeta) return
    setSaving(true)
    setError(null)
    try {
      const payload = toPayload(appointment, form)
      await supportCenterAttendService.submitPsychAttendance(payload)
      setConfirmCompleteOpen(false)
      onSubmitted()
    } catch (e) {
      setConfirmCompleteOpen(false)
      setError(
        e instanceof Error
          ? e.message
          : 'Não foi possível salvar o atendimento.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <Text size="sm" color="muted">
        Documento sigiloso — uso exclusivo do profissional. Os dados clínicos são gravados no QG
        ao concluir.
      </Text>

      <div className={sectionClass}>
        <Text size="xs" className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
          Demanda trazida
        </Text>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            O que o(a) segurado(a) trouxe neste atendimento? <span className="text-red-500">*</span>
          </label>
          <textarea
            className={`${inputClass} min-h-[80px]`}
            value={form.demand_notes}
            onChange={(e) => setForm((prev) => ({ ...prev, demand_notes: e.target.value }))}
            disabled={saving || loadingMeta}
            placeholder="Queixa, situação ou tema apresentado..."
          />
        </div>
      </div>

      <div className={sectionClass}>
        <Text size="xs" className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
          Conduta e intervenção
        </Text>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Intervenção realizada <span className="text-red-500">*</span>
          </label>
          <textarea
            className={`${inputClass} min-h-[80px]`}
            value={form.intervention_notes}
            onChange={(e) => setForm((prev) => ({ ...prev, intervention_notes: e.target.value }))}
            disabled={saving || loadingMeta}
            placeholder="Escuta, orientação, técnica utilizada..."
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Resposta do(a) segurado(a)
          </label>
          <textarea
            className={`${inputClass} min-h-[60px]`}
            value={form.patient_response}
            onChange={(e) => setForm((prev) => ({ ...prev, patient_response: e.target.value }))}
            disabled={saving || loadingMeta}
            placeholder="Como recebeu a intervenção?"
          />
        </div>
      </div>

      <div className={sectionClass}>
        <Text size="xs" className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
          Avaliação final
        </Text>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Estado emocional ao final <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES.map((state) => {
              const active = form.emotional_state === state
              return (
                <button
                  key={state}
                  type="button"
                  disabled={saving || loadingMeta}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      emotional_state: state as SupportCenterPsychEmotionalState,
                    }))
                  }
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary-400'
                  }`}
                >
                  {state}
                </button>
              )
            })}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Indicador de risco <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {SUPPORT_CENTER_PSYCH_RISK_LEVELS.map((level) => {
              const active = form.risk_indicator === level
              return (
                <button
                  key={level}
                  type="button"
                  disabled={saving || loadingMeta}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      risk_indicator: level as SupportCenterPsychRiskLevel,
                    }))
                  }
                  className={`rounded-lg px-2 py-3 text-xs font-semibold border text-center transition-colors ${
                    active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary-400'
                  }`}
                >
                  {level}
                </button>
              )
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Encaminhamento
            </label>
            <select
              className={inputClass}
              value={form.referral}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  referral: e.target.value as SupportCenterPsychReferral | '',
                }))
              }
              disabled={saving || loadingMeta}
            >
              <option value="">Selecione...</option>
              {SUPPORT_CENTER_PSYCH_REFERRALS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Próximo contato
            </label>
            <input
              type="date"
              className={inputClass}
              value={form.next_contact_date}
              onChange={(e) => setForm((prev) => ({ ...prev, next_contact_date: e.target.value }))}
              disabled={saving || loadingMeta}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Observações adicionais
          </label>
          <textarea
            className={`${inputClass} min-h-[60px]`}
            value={form.additional_notes}
            onChange={(e) => setForm((prev) => ({ ...prev, additional_notes: e.target.value }))}
            disabled={saving || loadingMeta}
            placeholder="Combinados, alertas, contexto relevante..."
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {actionsHost
        ? createPortal(
            <>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button
                onClick={requestCompleteConfirm}
                disabled={saving || loadingMeta || !canSubmit}
              >
                {saving ? 'Salvando...' : 'Concluir atendimento'}
              </Button>
            </>,
            actionsHost
          )
        : null}

      {confirmCompleteOpen &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Concluir atendimento
              </Heading>
              <Text size="sm" color="muted">
                Ao confirmar, o prontuário será gravado e o atendimento marcado como realizado.
                Deseja continuar?
              </Text>
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setConfirmCompleteOpen(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button onClick={() => void handleSubmit()} disabled={saving}>
                  {saving ? 'Salvando...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
